import { Injectable } from '@angular/core';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { LoggerService } from '@core/services/logger/logger.service';
import { BiLoggerService } from '@core/services/logger/bi-logger.service';
import { TracesQuery } from '@shared/store/traces/traces-query';
import { RxFormCompletionLevel } from '@shared/models/rx-models/enums/rx-form-completion-level-enum';
import { ValidateRxService } from '@shared/services/validate-rx';
import { DentureDetailsUtils } from '@modules/denture-details/utils/denture-details-utils';
import { OrderQuery } from '@modules/order/state/order-query';
import { DentureDetailsQuery } from '@modules/denture-details/state/denture-details.query';

@Injectable({
	providedIn: 'root'
})
export class RxSummaryService {
	private readonly serviceName = 'RxSummaryService';

	constructor(
		private readonly shellQuery: ShellQuery,
		private readonly orderQuery: OrderQuery,
		private readonly dentureDetailsQuery: DentureDetailsQuery,
		private readonly validateRxService: ValidateRxService,
		private readonly logger: LoggerService,
		private readonly biLogger: BiLoggerService,
		private readonly tracesQuery: TracesQuery
	) {}

	validateRxForSummaryPage(isTreatmentValidForSend: boolean): { valid: boolean; rxId: string | null } {
		const rxId = this.shellQuery.rxId;

		if (!rxId) {
			this.logger.error('Rx-App tried to send Rx without Rx-Id', { module: this.serviceName });

			return { valid: false, rxId: null };
		}

		const isValidOrderData = this.validateRxService.isOrderSectionValidForSend();
		const isValidDentureData = DentureDetailsUtils.isDentureDetailsEnabled(this.orderQuery.procedureMap?.ProcedureId ?? 0)
			? this.validateRxService.isDentureDetailsValidForSend(this.dentureDetailsQuery)
			: true;
		const isValidForSend = isValidOrderData && isValidDentureData && isTreatmentValidForSend;

		if (isValidForSend) {
			const completionLevel = this.shellQuery.getValue().rx?.CompletionLevel;

			void this.biLogger.reporter?.trackStage({
				object: 'rx',
				stage: 'send',
				previous: completionLevel != null ? RxFormCompletionLevel[completionLevel] : '',
				description: rxId,
				trigger: 'user'
			});

			return { valid: true, rxId };
		}

		this.logger.info('Attempt to send invalid RX', {
			module: this.serviceName,
			extendedParameters: {
				traces: this.tracesQuery.configurationTraceIdsAsString + this.tracesQuery.savingGettingRxTraceIds
			}
		});

		return { valid: false, rxId: null };
	}
}
